import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { 
  ReceiptData, 
  ReceiptItem, 
  ValidationError, 
  AssistReceiptItem,
  ReferralReceiptItem
} from '../types';
import { formatDate } from '../../../utils/dateUtils.ts';
import { toNumber } from '../../common/utils/validationUtils.ts';
import { DailyReportDebugLogger } from '../../../utils/debugUtils.ts';

interface ExcelProcessorResult {
  receiptData: ReceiptData;
  assistReceiptData: AssistReceiptItem[];
  assistCashierDoctorData: AssistReceiptItem[]; // 🆕 새로운 (수납자+진료의사 기준)
  referralReceiptData: ReferralReceiptItem[];
  reportStatusData: ReportStatusItem[];
  validationErrors: ValidationError[];
}

interface UseExcelProcessorReturn {
  processFile: (file: File) => Promise<ExcelProcessorResult>;
  extractDateFromFileName: (fileName: string) => string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * 엑셀 파일 처리 훅
 */
export const useExcelProcessor = (): UseExcelProcessorReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 파일명에서 날짜 추출
   * @param fileName - 파일명
   */
  const extractDateFromFileName = useCallback((fileName: string): string | null => {
    if (!fileName) return null;
    
    const dateMatch = fileName.match(/(\d{4}-\d{2}-\d{2})일계표\.xlsx/);
    return dateMatch && dateMatch[1] ? dateMatch[1] : null;
  }, []);

  /**
   * 어시스트 컬럼 이름 후보 — 원내 프로그램 내보내기는 '담당직원'을 쓴다.
   *
   * ⚠️ 원래 코드는 '어시스트' 한 이름만 읽었는데 그 컬럼은 이 엑셀에 존재하지 않는다.
   *    그래서 매번 빈 값 → 수납자 폴백이 발화해 **모든 진료의 어시스트가 수납자(원장)로 저장**됐다.
   *    2026-08 실측: 담당직원은 유정민 154·정은아 133·오소정 133·김민지 145·파트 69·한지미 6 (640건 전부)
   *    인데 저장된 어시스트는 김은자 496 / 미지정 144 였다.
   */
  //  ⚠️ '담당자'는 넣지 않는다 — 아래 cashierName 이 같은 이름을 수납자 후보로 쓴다.
  //     둘 다에 넣으면 한 컬럼이 어시스트이자 수납자로 잡혀, 이번에 없앤 '수납자를 어시스트로
  //     적는' 상태가 다른 형식의 파일에서 되살아난다.
  const ASSIST_COLUMNS = ['어시스트', '담당직원', '어시스턴트'];

  const pickAssistField = (item: any): any => {
    for (const key of ASSIST_COLUMNS) {
      if (item && item[key] !== undefined && String(item[key]).trim() !== '') return item[key];
    }
    return '';
  };

  /**
   * 어시스트명 결정. 값이 없으면 '미지정' — 수납자로 대체하지 않는다.
   *
   * 수납자는 '돈을 받은 사람'이지 '진료를 도운 사람'이 아니다. 폴백으로 채우면 누가 했는지를
   * 조용히 바꿔 적는 셈이고, 팀 귀속·어시스트별 실적이 통째로 틀어진다. 어시스트가 비면
   * '미지정'으로 남기고, 팀 귀속은 합의된 규칙(수납건 지정 → 의사 매핑 → 그날/그달 비율)이 맡는다.
   */
  const getEffectiveAssistName = (assistField: any): string => {
    const assistName = String(assistField || '').trim();
    const invalidAssistNames = ['미지정', '없음', '기타', ''];
    return invalidAssistNames.includes(assistName) ? '미지정' : assistName;
  };

  /**
   * 엑셀 데이터 처리 
   * @param jsonData - 엑셀에서 변환된 JSON 데이터
   * @param fileName - 파일명
   * @param defaultDate - 기본 날짜
   */
  const processExcelData = useCallback((
    jsonData: any[], 
    fileName?: string, 
    defaultDate: string = formatDate(new Date())
  ): ExcelProcessorResult => {
    // 환자 그룹화 데이터
    const patientGroups: Record<string, any> = {};
    const assistGroups: Record<string, any> = {};
    const assistCashierDoctorGroups: Record<string, any> = {}; // 🆕 새로운 그룹 변수
    const referralGroups: Record<string, any> = {};
    let totalReceipt = 0;
    
    DailyReportDebugLogger.log('EXCEL_UPLOAD','🚀 [useExcelProcessor] processExcelData 함수 시작 - 데이터 처리 중...');
    DailyReportDebugLogger.log('EXCEL_UPLOAD','🚀 [useExcelProcessor] 입력 데이터 개수:', jsonData.length);
    DailyReportDebugLogger.log('EXCEL_UPLOAD','🚀 [useExcelProcessor] 파일명:', fileName);
    DailyReportDebugLogger.log('EXCEL_UPLOAD','🚀 [useExcelProcessor] 기본 날짜:', defaultDate);
    
    // 첫 번째 데이터의 모든 컬럼명 확인
    if (jsonData.length > 0) {
      DailyReportDebugLogger.log('EXCEL_UPLOAD','[엑셀 컬럼명 확인] 첫 번째 행의 모든 컬럼:', Object.keys(jsonData[0]));
      DailyReportDebugLogger.log('EXCEL_UPLOAD','[엑셀 데이터 샘플] 첫 번째 행 데이터:', jsonData[0]);
      
      // 어시스트 관련 컬럼 상세 확인
      const firstRow = jsonData[0];
      DailyReportDebugLogger.log('EXCEL_UPLOAD','[어시스트 컬럼 상세 확인]:');
      Object.keys(firstRow).forEach(key => {
        if (key.includes('어시') || key.includes('담당') || key.includes('의')) {
          DailyReportDebugLogger.log('EXCEL_UPLOAD',`  - 컬럼명: "${key}" (길이: ${key.length}, 코드: ${key.split('').map(c => c.charCodeAt(0)).join(', ')})`);
          DailyReportDebugLogger.log('EXCEL_UPLOAD',`    값: "${firstRow[key]}"`);
        }
      });
      
      // 정확히 '어시스트' 컬럼 확인
      DailyReportDebugLogger.log('EXCEL_UPLOAD',`[정확한 매칭 테스트] firstRow['어시스트'] = "${firstRow['어시스트']}"`);
      DailyReportDebugLogger.log('EXCEL_UPLOAD',`[정확한 매칭 테스트] '어시스트' in firstRow = ${('어시스트' in firstRow)}`);
    }
    
    // 데이터 처리
    jsonData.forEach((item, index) => {
      // 환자 정보 추출
      let patientName = item['환자'] || item['이름'] || item['환자명'] || item['성명'] || '이름없음';
      
      // 디버깅: 건강생활유지비 데이터 확인
      if (item['건강생활유지비승인'] > 0 || item['건강생활유지비'] > 0) {
        DailyReportDebugLogger.log('EXCEL_UPLOAD','[건강생활유지비 데이터 발견]', {
          환자명: patientName,
          차트번호: item['차트번호'],
          건강생활유지비승인: item['건강생활유지비승인'],
          건강생활유지비: item['건강생활유지비'],
          총진료비: item['총진료비'],
          진료일: item['진료일']
        });
      }
      
      // 이름 마스킹 처리
      if (patientName.length >= 2) {
        patientName = patientName.charAt(0) + '*' + patientName.substring(2);
      }
      
      const chartNumber = item['차트번호'] || item['차트'] || item['번호'] || item['환자번호'] || '';
      // 🆕 수납자 정보를 별도로 추출
      const cashierName = String(item['수납자'] || item['담당자'] || item['수납담당'] || '').trim() || null;
      // 어시스트는 '담당직원' 등 별칭 컬럼에서 읽는다. 비면 '미지정' (수납자로 대체하지 않는다)
      const rawAssistField = pickAssistField(item);
      const assistName = getEffectiveAssistName(rawAssistField);
      // 🆕 진료의사 정보를 별도로 추출
      const doctorName = String(item['진료의사'] || '').trim() || null;
      const customerType = item['고객구분'] || '없음';
      const referralPath = item['내원경로'] || item['유입경로'] || item['방문경로'] || '없음';
      
      // 디버깅: 어시스트 및 진료구분 데이터 확인
      if (index < 5) {
        DailyReportDebugLogger.log('EXCEL_UPLOAD',`[어시스트 데이터] 행 ${index + 1}:`, {
          원본_어시스트: rawAssistField,
          어시스트컬럼후보: ASSIST_COLUMNS.filter(k => item[k] !== undefined),
          원본_진료의사: item['진료의사'],
          원본_수납자: item['수납자'],
          최종어시스트명: assistName,   // getEffectiveAssistName 함수 결과
          진료의사명: doctorName,
          수납자명: cashierName,  // 🆕 수납자 정보 추가
          진료의사사용여부: false,  // 더이상 진료의사를 어시스트명에 사용하지 않음
          모든컬럼명: Object.keys(item).filter(key => 
            key.toLowerCase().includes('담당') || 
            key.toLowerCase().includes('의') || 
            key.toLowerCase().includes('assist') ||
            key.toLowerCase().includes('어시') ||
            key.toLowerCase().includes('수납'))
        });
        
        DailyReportDebugLogger.log('EXCEL_UPLOAD',`[진료구분 데이터] 행 ${index + 1}:`, {
          구분: item['구분'],
          환자구분: item['환자구분'],
          진료구분: item['진료구분'],
          종류: item['종류'],
          최종진료구분: customerType,
          구분관련컬럼: Object.keys(item).filter(key => 
            key.includes('구분') || 
            key.includes('종류') ||
            key.includes('타입') ||
            key.includes('type') ||
            key.includes('Type'))
        });
      }
      
      // 영수증 날짜 (기본값으로 현재 날짜 사용)
      let receiptDate = defaultDate;
      
      // 날짜 필드에서 날짜 추출 (YYYY-MM-DD 형식)
      // '진료일' 필드를 우선적으로 확인
      const dateField = item['진료일'] || item['날짜'] || item['일자'];
      
      if (dateField) {
        // 날짜 형식 'YYYY-MM-DD'인 경우 그대로 사용
        if (typeof dateField === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateField)) {
          receiptDate = dateField;
        }
        // 다른 형식의 문자열인 경우
        else if (typeof dateField === 'string') {
          try {
            const date = new Date(dateField);
            if (!isNaN(date.getTime())) {
              receiptDate = formatDate(date);
            }
          } catch (e) {
            // 날짜 변환 실패 시 기본값 사용
            DailyReportDebugLogger.log('EXCEL_UPLOAD','날짜 변환 실패:', dateField, e);
          }
        } 
        // 숫자 형식 날짜인 경우 (Excel의 시리얼 날짜로 간주)
        else if (typeof dateField === 'number') {
          try {
            // Excel 기준 날짜 (1900년 1월 1일부터 시작)
            const excelEpoch = new Date(1899, 11, 30);
            const date = new Date(excelEpoch.getTime() + dateField * 24 * 60 * 60 * 1000);
            if (!isNaN(date.getTime())) {
              receiptDate = formatDate(date);
            }
          } catch (e) {
            // 날짜 변환 실패 시 기본값 사용
            DailyReportDebugLogger.log('EXCEL_UPLOAD','Excel 날짜 변환 실패:', dateField, e);
          }
        }
      }
      
      // 파일명에서 날짜 정보 추출은 백업용으로만 사용
      // (개별 데이터의 날짜가 없을 때만 적용)
      if (receiptDate === defaultDate && fileName && fileName.includes('일계표.xlsx')) {
        const fileNameDateMatch = fileName.match(/(\d{4}-\d{2}-\d{2})일계표\.xlsx/);
        if (fileNameDateMatch && fileNameDateMatch[1]) {
          // 파일명에서 추출한 날짜 사용
          receiptDate = fileNameDateMatch[1];
        }
      }
      
      // 디버깅: 날짜 처리 결과 확인
      if (index < 5) { // 처음 5개 항목만 로그
        DailyReportDebugLogger.log('EXCEL_UPLOAD',`[날짜 처리] 행 ${index + 1}: 진료일=${item['진료일']}, 최종날짜=${receiptDate}`);
      }
      
      // 각 환자별 수납 데이터 - 날짜별로 구분
      const patientKey = `${patientName}_${chartNumber}_${receiptDate}`;
      if (!patientGroups[patientKey]) {
        patientGroups[patientKey] = {
          chartNumber: chartNumber,
          name: patientName,
          assistName: assistName, // 어시스트명
          doctorName: doctorName, // 🆕 진료의사명 추가
          cashierName: cashierName, // 🆕 수납자명 추가
          count: 0,
          totalFee: 0,
          actualReceiptAmount: 0, // 🆕 실제 수납액 필드 추가
          insuranceClaim: 0,
          personalCharge: 0,
          nonCovered: 0,
          valueAdded: 0,
          discount: 0,
          cashPayment: 0,
          cardPayment: 0,
          onlinePayment: 0,
          uncollectedPayment: 0,
          healthLifeSupport: 0,
          receiptDate: receiptDate
        };
      }
      
      // 🔄 기존: 어시스트+진료구분 조합으로 그룹화 (기존 로직 유지)
      const assistKey = `${assistName}_${customerType}_${receiptDate}`;
      if (!assistGroups[assistKey]) {
        assistGroups[assistKey] = {
          name: assistName,
          doctorName: doctorName,        // 진료의사명
          cashierName: cashierName,      // 🆕 수납자명 추가
          treatmentType: customerType, // 기존 진료구분 유지
          count: 0,
          totalFee: 0,
          insuranceClaim: 0,
          personalCharge: 0,
          nonCovered: 0,
          valueAdded: 0,
          discount: 0,
          cashPayment: 0,
          cardPayment: 0,
          onlinePayment: 0,
          uncollectedPayment: 0,
          healthLifeSupport: 0,
          receiptDate: receiptDate
        };
      }
      
      // 🆕 새로운: 어시스트+수납자+진료의사 조합으로 그룹화
      const assistCashierDoctorKey = `${assistName}_${cashierName || 'NoC'}_${doctorName || 'NoD'}_${receiptDate}`;
      if (!assistCashierDoctorGroups[assistCashierDoctorKey]) {
        // 수납자와 진료의사 정보가 있는지 체크
        const hasCashier = cashierName && cashierName.trim() !== '';
        const hasDoctor = doctorName && doctorName.trim() !== '';
        
        // treatmentType을 수납자+진료의사 조합으로 설정
        let treatmentDescription = '';
        if (hasCashier && hasDoctor) {
          treatmentDescription = `${cashierName}-${doctorName}`;
        } else if (hasCashier) {
          treatmentDescription = `${cashierName}-미지정의사`;
        } else if (hasDoctor) {
          treatmentDescription = `미지정수납자-${doctorName}`;
        } else {
          treatmentDescription = '미지정수납자-미지정의사';
        }
        
        assistCashierDoctorGroups[assistCashierDoctorKey] = {
          name: assistName,
          doctorName: doctorName,
          cashierName: cashierName,
          treatmentType: treatmentDescription, // 🆕 수납자-진료의사 조합
          count: 0,
          totalFee: 0,
          insuranceClaim: 0,
          personalCharge: 0,
          nonCovered: 0,
          valueAdded: 0,
          discount: 0,
          cashPayment: 0,
          cardPayment: 0,
          onlinePayment: 0,
          uncollectedPayment: 0,
          healthLifeSupport: 0,
          receiptDate: receiptDate
        };
        
        // 디버깅 로그
        if (index < 5) {
          DailyReportDebugLogger.log('EXCEL_UPLOAD',`[새로운 assistCashierDoctorKey 생성] 행 ${index + 1}:`, {
            assistCashierDoctorKey: assistCashierDoctorKey,
            assistName: assistName,
            cashierName: cashierName,
            doctorName: doctorName,
            treatmentType: treatmentDescription,
            hasCashier: hasCashier,
            hasDoctor: hasDoctor
          });
        }
      }
      
      // 내원경로별 그룹화 (담당의별 경로) - 날짜별로 구분
      const referralKey = `${referralPath}_${assistName}_${receiptDate}`;
      if (!referralGroups[referralKey]) {
        referralGroups[referralKey] = {
          path: referralPath,
          assistName: assistName,
          count: 0,
          totalFee: 0,
          insuranceClaim: 0,
          personalCharge: 0,
          nonCovered: 0,
          valueAdded: 0,
          discount: 0,
          cashPayment: 0,
          cardPayment: 0,
          onlinePayment: 0,
          uncollectedPayment: 0,
          healthLifeSupport: 0,
          receiptDate: receiptDate
        };
      }
      
      // 금액 데이터 추출 - 디버깅 추가
      if (index < 3) {
        DailyReportDebugLogger.log('EXCEL_UPLOAD',`[금액 데이터 디버깅] 행 ${index + 1}:`);
        DailyReportDebugLogger.log('EXCEL_UPLOAD','  원본 데이터:', item);
        DailyReportDebugLogger.log('EXCEL_UPLOAD','  모든 필드와 값:');
        Object.entries(item).forEach(([key, value]) => {
          DailyReportDebugLogger.log('EXCEL_UPLOAD',`    - "${key}": "${value}" (타입: ${typeof value})`);
        });
        
        // toNumber 함수 테스트
        DailyReportDebugLogger.log('EXCEL_UPLOAD','  toNumber 테스트:');
        DailyReportDebugLogger.log('EXCEL_UPLOAD',`    - toNumber(item['총진료비']): ${toNumber(item['총진료비'])}`);
        DailyReportDebugLogger.log('EXCEL_UPLOAD',`    - toNumber(item['어시스트']): ${toNumber(item['어시스트'])}`);
        DailyReportDebugLogger.log('EXCEL_UPLOAD',`    - item['어시스트'] 원본값: "${item['어시스트']}"`);
      }
      
      const totalFee = toNumber(item['총진료비'] || item['진료비'] || item['금액'] || item['합계']);
      const insuranceClaim = toNumber(item['청구액'] || item['보험'] || item['보험금액'] || item['청구']);
      const personalCharge = toNumber(item['본인부담금'] || item['본인부담'] || item['부담금'] || item['본인']);
      const nonCovered = toNumber(item['비급여'] || item['비급여금'] || item['비급여액']);
      const valueAdded = toNumber(item['부가가치세'] || item['부가세'] || item['세금'] || item['VAT']);
      const discount = toNumber(item['할인액'] || item['할인금'] || item['할인'] || item['감면액']);

      // 🔍 [버그C 진단] 할인액이 0보다 클 때만 로그 출력 (첫 5건만)
      if (index < 5 && (item['할인액'] || item['할인금'] || item['할인'] || item['감면액'])) {
        console.log('🔍 [할인액 디버그]', {
          index,
          환자명: item['이름'] || item['환자명'],
          할인액_원본: item['할인액'],
          할인금_원본: item['할인금'],
          할인_원본: item['할인'],
          감면액_원본: item['감면액'],
          toNumber결과: discount,
          모든키: Object.keys(item).filter(k => k.includes('할인') || k.includes('감면'))
        });
      }

      const cashPayment = toNumber(item['현금수납'] || item['현금'] || item['현'] || item['현장'] || item['현장결제']);
      const cardPayment = toNumber(item['카드수납'] || item['카드'] || item['카'] || item['카결'] || item['신용카드']);
      const onlinePayment = toNumber(item['기타(온라인)'] || item['온라인(계좌)'] || item['계좌'] || item['계좌이체'] || item['온라인']);
      const uncollectedPayment = toNumber(item['미수(+)선수(-)'] || item['미수(+) 선수(-)'] || item['미수'] || item['체납'] || item['미납'] || item['체불']);
      const healthLifeSupport = toNumber(item['건강생활유지비승인'] || item['건강생활유지비'] || item['생활유지비']);

      // 🎯 실제 수납액 계산 (총진료비 - 할인액 - 미수금)
      const actualReceiptAmount = totalFee - discount - uncollectedPayment;
      
      // 환자별 합계 누적
      patientGroups[patientKey].count++;
      patientGroups[patientKey].totalFee += totalFee; // 원본 총진료비 유지 (검증용)
      patientGroups[patientKey].actualReceiptAmount = (patientGroups[patientKey].actualReceiptAmount || 0) + actualReceiptAmount; // 🆕 실제 수납액 추가
      patientGroups[patientKey].insuranceClaim += insuranceClaim;
      patientGroups[patientKey].personalCharge += personalCharge;
      patientGroups[patientKey].nonCovered += nonCovered;
      patientGroups[patientKey].valueAdded += valueAdded;
      patientGroups[patientKey].discount += discount;
      patientGroups[patientKey].cashPayment += cashPayment;
      patientGroups[patientKey].cardPayment += cardPayment;
      patientGroups[patientKey].onlinePayment += onlinePayment;
      patientGroups[patientKey].uncollectedPayment += uncollectedPayment;
      patientGroups[patientKey].healthLifeSupport += healthLifeSupport;
      
      // 🔄 기존: 어시스트+진료구분 조합별 합계 누적 (실제 수납액 기준)
      assistGroups[assistKey].count++;
      assistGroups[assistKey].totalFee += actualReceiptAmount; // 🎯 실제 수납액으로 변경
      assistGroups[assistKey].insuranceClaim += insuranceClaim;
      assistGroups[assistKey].personalCharge += personalCharge;
      assistGroups[assistKey].nonCovered += nonCovered;
      assistGroups[assistKey].valueAdded += valueAdded;
      assistGroups[assistKey].discount += discount;
      assistGroups[assistKey].cashPayment += cashPayment;
      assistGroups[assistKey].cardPayment += cardPayment;
      assistGroups[assistKey].onlinePayment += onlinePayment;
      assistGroups[assistKey].uncollectedPayment += uncollectedPayment;
      assistGroups[assistKey].healthLifeSupport += healthLifeSupport;
      
      // 🆕 새로운: 어시스트+수납자+진료의사 조합별 합계 누적 (실제 수납액 기준)
      assistCashierDoctorGroups[assistCashierDoctorKey].count++;
      assistCashierDoctorGroups[assistCashierDoctorKey].totalFee += actualReceiptAmount; // 🎯 실제 수납액으로 변경
      assistCashierDoctorGroups[assistCashierDoctorKey].insuranceClaim += insuranceClaim;
      assistCashierDoctorGroups[assistCashierDoctorKey].personalCharge += personalCharge;
      assistCashierDoctorGroups[assistCashierDoctorKey].nonCovered += nonCovered;
      assistCashierDoctorGroups[assistCashierDoctorKey].valueAdded += valueAdded;
      assistCashierDoctorGroups[assistCashierDoctorKey].discount += discount;
      assistCashierDoctorGroups[assistCashierDoctorKey].cashPayment += cashPayment;
      assistCashierDoctorGroups[assistCashierDoctorKey].cardPayment += cardPayment;
      assistCashierDoctorGroups[assistCashierDoctorKey].onlinePayment += onlinePayment;
      assistCashierDoctorGroups[assistCashierDoctorKey].uncollectedPayment += uncollectedPayment;
      assistCashierDoctorGroups[assistCashierDoctorKey].healthLifeSupport += healthLifeSupport;
      
      // 내원경로별 합계 누적 (실제 수납액 기준)
      referralGroups[referralKey].count++;
      referralGroups[referralKey].totalFee += actualReceiptAmount; // 🎯 실제 수납액으로 변경
      referralGroups[referralKey].insuranceClaim += insuranceClaim;
      referralGroups[referralKey].personalCharge += personalCharge;
      referralGroups[referralKey].nonCovered += nonCovered;
      referralGroups[referralKey].valueAdded += valueAdded;
      referralGroups[referralKey].discount += discount;
      referralGroups[referralKey].cashPayment += cashPayment;
      referralGroups[referralKey].cardPayment += cardPayment;
      referralGroups[referralKey].onlinePayment += onlinePayment;
      referralGroups[referralKey].uncollectedPayment += uncollectedPayment;
      referralGroups[referralKey].healthLifeSupport += healthLifeSupport;
      
      totalReceipt += actualReceiptAmount; // 🎯 실제 수납액으로 변경
    });
    
    // 유효성 검사 및 환자 데이터로 변환
    const validationErrors: ValidationError[] = [];
    const receiptItems: ReceiptItem[] = [];
    
    DailyReportDebugLogger.log('EXCEL_UPLOAD','환자 그룹 수:', Object.keys(patientGroups).length);
    DailyReportDebugLogger.log('EXCEL_UPLOAD','금액 검증 시작...');
    DailyReportDebugLogger.log('EXCEL_UPLOAD','환자 그룹 전체 데이터:', patientGroups);
    
    Object.values(patientGroups).forEach((patient: any) => {
      // 🎯 실제 수납액 기준 검증 (미수금 제외한 현금 흐름만)
      // 실제수납액 = 현금 + 카드 + 온라인 + 보험금 + 건강생활유지비 - 부가가치세 (미수금 제외)
      const paymentSum = (patient.cashPayment || 0) + 
                        (patient.onlinePayment || 0) + 
                        (patient.cardPayment || 0) + 
                        // (patient.uncollectedPayment || 0) +  // 🎯 미수금 제외 (실제 현금 흐름 아님)
                        (patient.insuranceClaim || 0) +
                        (patient.healthLifeSupport || 0) -
                        (patient.valueAdded || 0);  // 🎯 부가가치세 차감
      
      // 🎯 실제 수납액과 비교 (총진료비 - 할인액 - 미수금)
      const actualReceiptAmount = patient.totalFee - (patient.discount || 0) - (patient.uncollectedPayment || 0);
      const hasError = Math.abs(paymentSum - actualReceiptAmount) > 0.01;
      
      if (hasError) {
        DailyReportDebugLogger.error('ERROR_HANDLING','=== 🚨 금액 불일치 발생 🚨 ===');
        DailyReportDebugLogger.error('ERROR_HANDLING',`환자명: ${patient.name}`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`차트번호: ${patient.chartNumber}`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`날짜: ${patient.receiptDate}`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`총진료비: ${patient.totalFee.toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`할인액: ${(patient.discount || 0).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`미수금: ${(patient.uncollectedPayment || 0).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`실제수납액: ${actualReceiptAmount.toLocaleString()}원 (총진료비 - 할인액 - 미수금)`);
        DailyReportDebugLogger.error('ERROR_HANDLING','수납 내역 (실제 현금 흐름):');
        DailyReportDebugLogger.error('ERROR_HANDLING',`  - 현금: ${(patient.cashPayment || 0).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`  - 계좌이체: ${(patient.onlinePayment || 0).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`  - 카드: ${(patient.cardPayment || 0).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`  - 보험금: ${(patient.insuranceClaim || 0).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`  - 건강생활유지비: ${(patient.healthLifeSupport || 0).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`  - 부가가치세(차감): -${(patient.valueAdded || 0).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`실제수납 합계: ${paymentSum.toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING',`차액: ${(actualReceiptAmount - paymentSum).toLocaleString()}원`);
        DailyReportDebugLogger.error('ERROR_HANDLING','============================');
        
        validationErrors.push({
          patientName: patient.name,
          chartNumber: patient.chartNumber,
          totalFee: actualReceiptAmount, // 🎯 실제 수납액으로 변경
          paymentSum: paymentSum,
          diff: actualReceiptAmount - paymentSum, // 🎯 실제 수납액 기준 차이
          receiptDate: patient.receiptDate
        });
      }
      
      // 건강생활유지비 데이터 최종 확인
      if (patient.healthLifeSupport > 0) {
        DailyReportDebugLogger.log('EXCEL_UPLOAD','[건강생활유지비 receiptItem 생성]', {
          환자명: patient.name,
          차트번호: patient.chartNumber,
          날짜: patient.receiptDate,
          건강생활유지비: patient.healthLifeSupport,
          총진료비: patient.totalFee,
          실제수납액: actualReceiptAmount
        });
      }
      
      receiptItems.push({
        chartNumber: patient.chartNumber,
        name: patient.name,
        assistName: patient.assistName, // 어시스트명
        doctorName: patient.doctorName, // 🆕 진료의사명 추가
        cashierName: patient.cashierName, // 🆕 수납자명 추가
        insuranceType: '',
        paymentMethod: '',
        cardCompany: '',
        amount: actualReceiptAmount, // 🎯 실제 수납액으로 변경 (총진료비 - 할인액 - 미수금)
        cashAmount: patient.cashPayment,
        transferAmount: patient.onlinePayment,
        cardAmount: patient.cardPayment,
        uncollectedAmount: patient.uncollectedPayment,
        insuranceClaim: patient.insuranceClaim,
        healthLifeSupport: patient.healthLifeSupport,
        discount: patient.discount || 0, // 🆕 할인액 추가
        valueAdded: patient.valueAdded, // 부가가치세 추가
        hasError,
        receiptDate: patient.receiptDate || formatDate(new Date())
      });
    });
    
    // 🔄 기존: 어시스트별 집계 변환 (진료구분 기준)
    DailyReportDebugLogger.log('EXCEL_UPLOAD','[어시스트 집계 - 진료구분 기준] assistGroups:', assistGroups);
    const assistReceiptItems: AssistReceiptItem[] = Object.values(assistGroups).map((group: any) => ({
      assistName: group.name,
      doctorName: group.doctorName,    // 진료의사명
      cashierName: group.cashierName,  // 🆕 수납자명 추가
      treatmentType: group.treatmentType, // 진료구분 (신환, 재진, 검진)
      count: group.count,
      amount: group.totalFee,
      discount: group.discount || 0,   // 🆕 할인액 추가
      receiptDate: group.receiptDate || formatDate(new Date())
    }));
    
    // 🆕 새로운: 어시스트별 집계 변환 (수납자+진료의사 조합 기준)
    DailyReportDebugLogger.log('EXCEL_UPLOAD','[어시스트 집계 - 수납자+진료의사 기준] assistCashierDoctorGroups:', assistCashierDoctorGroups);
    const assistCashierDoctorItems: AssistReceiptItem[] = Object.values(assistCashierDoctorGroups).map((group: any) => ({
      assistName: group.name,
      doctorName: group.doctorName,
      cashierName: group.cashierName,
      treatmentType: group.treatmentType, // 수납자-진료의사 조합
      count: group.count,
      amount: group.totalFee,
      discount: group.discount || 0,   // 🆕 할인액 추가
      receiptDate: group.receiptDate || formatDate(new Date())
    }));
    
    DailyReportDebugLogger.log('EXCEL_UPLOAD','[어시스트 집계] 기존 방식 항목 수:', assistReceiptItems.length);
    DailyReportDebugLogger.log('EXCEL_UPLOAD','[어시스트 집계] 새로운 방식 항목 수:', assistCashierDoctorItems.length);
    
    // 🆕 수납자/진료의사 조합별 통계
    const combinationStats = assistCashierDoctorItems.reduce((stats, item) => {
      const combo = item.treatmentType;
      if (!stats[combo]) {
        stats[combo] = { count: 0, totalAmount: 0, assistNames: new Set() };
      }
      stats[combo].count += item.count;
      stats[combo].totalAmount += item.amount;
      stats[combo].assistNames.add(item.assistName);
      return stats;
    }, {} as Record<string, { count: number; totalAmount: number; assistNames: Set<string> }>);
    
    DailyReportDebugLogger.log('EXCEL_UPLOAD','[어시스트 집계] 수납자-진료의사 조합별 통계:', 
      Object.entries(combinationStats).map(([combo, data]) => ({
        조합: combo,
        건수: data.count,
        금액: data.totalAmount.toLocaleString() + '원',
        어시스트수: data.assistNames.size
      }))
    );
    
    // 내원경로별 집계 변환
    const referralReceiptItems: ReferralReceiptItem[] = Object.values(referralGroups).map((group: any) => ({
      referralPath: group.path,
      assistName: group.assistName,
      count: group.count,
      amount: group.totalFee,
      receiptDate: group.receiptDate || formatDate(new Date())
    }));
    
    // 보고서 집계 변환
    const reportStatusData: ReportStatusItem[] = [
      {
        office: '본원',
        insuranceType: '건강보험',
        insuranceFee: Object.values(assistGroups).reduce((sum: number, group: any) => sum + group.insuranceClaim, 0),
        personalCharge: Object.values(assistGroups).reduce((sum: number, group: any) => sum + group.personalCharge, 0),
        nonCovered: Object.values(assistGroups).reduce((sum: number, group: any) => sum + group.nonCovered, 0),
        valueAdded: Object.values(assistGroups).reduce((sum: number, group: any) => sum + group.valueAdded, 0)
      }
    ];
    
    // 날짜별 데이터 분포 확인
    const dateDistribution: Record<string, number> = {};
    receiptItems.forEach(item => {
      dateDistribution[item.receiptDate] = (dateDistribution[item.receiptDate] || 0) + 1;
    });
    DailyReportDebugLogger.log('EXCEL_UPLOAD','날짜별 데이터 분포:', dateDistribution);
    
    return {
      receiptData: {
        total: totalReceipt,
        items: receiptItems
      },
      assistReceiptData: assistReceiptItems, // 🔄 기존 방식 (진료구분 기준)
      assistCashierDoctorData: assistCashierDoctorItems, // 🆕 새로운 방식 (수납자+진료의사 기준)
      referralReceiptData: referralReceiptItems,
      reportStatusData,
      validationErrors
    };
  }, []);

  /**
   * 엑셀 파일 처리
   * @param file - 업로드된 엑셀 파일
   */
  const processFile = useCallback(async (file: File): Promise<ExcelProcessorResult> => {
    DailyReportDebugLogger.log('EXCEL_UPLOAD','🚀 [useExcelProcessor] processFile 함수 시작');
    DailyReportDebugLogger.log('EXCEL_UPLOAD','🚀 [useExcelProcessor] 파일 정보:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: new Date(file.lastModified).toLocaleString()
    });
    
    setIsLoading(true);
    setError(null);
    
    try {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { 
              type: 'array',
              cellFormula: false,  // 수식 처리 비활성화
              cellHTML: false,
              cellNF: false,
              cellStyles: false,
              cellText: true,
              cellDates: true,
              dateNF: 'yyyy-mm-dd',
              sheetStubs: false,
              sheetRows: 0,
              raw: false,
              WTF: false  // 엄격한 오류 처리 비활성화
            });
            
            // 모든 시트 이름 출력
            DailyReportDebugLogger.log('EXCEL_UPLOAD','[엑셀 시트 정보] 전체 시트 목록:', workbook.SheetNames);
            DailyReportDebugLogger.log('EXCEL_UPLOAD','[엑셀 시트 정보] 시트 개수:', workbook.SheetNames.length);
            
            if (workbook.SheetNames.length > 1) {
              // 두 번째 시트부터 모든 시트 처리
              const sheetsToProcess = workbook.SheetNames.slice(1); // 첫 번째 시트 제외
              DailyReportDebugLogger.log('EXCEL_UPLOAD',`[엑셀 시트 정보] 처리할 시트들:`, sheetsToProcess);
              
              let allJsonData: any[] = [];
              
              // 각 시트를 순회하며 데이터 수집
              sheetsToProcess.forEach((sheetName, index) => {
                DailyReportDebugLogger.log('EXCEL_UPLOAD',`[엑셀 시트 처리] ${index + 2}번째 시트: "${sheetName}" 처리 중...`);
                
                const worksheet = workbook.Sheets[sheetName];
                
                // 시트의 범위 확인
                const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
                DailyReportDebugLogger.log('EXCEL_UPLOAD',`[시트 "${sheetName}" 범위]:`, {
                  시작: `${XLSX.utils.encode_cell(range.s)}`,
                  끝: `${XLSX.utils.encode_cell(range.e)}`,
                  행수: range.e.r - range.s.r + 1,
                  열수: range.e.c - range.s.c + 1
                });
                
                // 빈 시트인지 확인
                if (range.e.r < range.s.r || range.e.c < range.s.c) {
                  DailyReportDebugLogger.log('EXCEL_UPLOAD',`[시트 "${sheetName}"] 빈 시트이므로 건너뛰기`);
                  return;
                }
                
                try {
                  // 첫 번째 행(헤더) 직접 읽기
                  DailyReportDebugLogger.log('EXCEL_UPLOAD',`[시트 "${sheetName}" 헤더 직접 읽기]:`);
                  for (let col = range.s.c; col <= range.e.c; col++) {
                    const cellAddress = XLSX.utils.encode_cell({r: range.s.r, c: col});
                    const cell = worksheet[cellAddress];
                    if (cell) {
                      DailyReportDebugLogger.log('EXCEL_UPLOAD',`  - ${cellAddress}: "${cell.v}" (타입: ${cell.t})`);
                    }
                  }
                  
                  // 시트 데이터 읽기
                  const sheetJsonData = XLSX.utils.sheet_to_json(worksheet, {
                    defval: '',
                    raw: false,
                    dateNF: 'yyyy-mm-dd',
                    blankrows: false
                  });
                  
                  if (sheetJsonData && sheetJsonData.length > 0) {
                    DailyReportDebugLogger.log('EXCEL_UPLOAD',`[시트 "${sheetName}"] 데이터 ${sheetJsonData.length}개 행 발견`);
                    allJsonData = allJsonData.concat(sheetJsonData);
                  } else {
                    DailyReportDebugLogger.log('EXCEL_UPLOAD',`[시트 "${sheetName}"] 데이터 없음`);
                  }
                } catch (sheetError) {
                  DailyReportDebugLogger.error('ERROR_HANDLING',`[시트 "${sheetName}"] 읽기 오류:`, sheetError);
                  // 해당 시트는 건너뛰고 다음 시트 계속 처리
                }
              });
              
              if (allJsonData.length === 0) {
                setError('처리 가능한 데이터가 있는 시트가 없습니다');
                reject(new Error('처리 가능한 데이터가 있는 시트가 없습니다'));
                return;
              }
              
              DailyReportDebugLogger.log('EXCEL_UPLOAD',`[전체 데이터] 총 ${allJsonData.length}개 행 수집 완료`);
              
              // 컬럼명 정규화 (공백 제거)
              const normalizedData = allJsonData.map((row: any) => {
                const normalizedRow: any = {};
                Object.keys(row).forEach(key => {
                  const normalizedKey = key.trim();
                  normalizedRow[normalizedKey] = row[key];
                });
                return normalizedRow;
              });
              
              // 엑셀 데이터 처리
              DailyReportDebugLogger.log('EXCEL_UPLOAD','통합 엑셀 파일 처리 시작:', file.name);
              DailyReportDebugLogger.log('EXCEL_UPLOAD','통합 데이터 개수:', normalizedData.length);
              
              const processedData = processExcelData(normalizedData, file.name);
              
              DailyReportDebugLogger.log('EXCEL_UPLOAD','처리 완료 - 금액 불일치 건수:', processedData.validationErrors.length);
              if (processedData.validationErrors.length > 0) {
                DailyReportDebugLogger.error('ERROR_HANDLING','🚨 금액 불일치가 발견되었습니다!');
                DailyReportDebugLogger.error('ERROR_HANDLING','불일치 목록:', processedData.validationErrors);
              }
              
              setIsLoading(false);
              resolve(processedData);
            } else if (workbook.SheetNames.length === 1) {
              // 시트가 하나만 있는 경우 첫 번째 시트 사용
              const sheetName = workbook.SheetNames[0];
              DailyReportDebugLogger.log('EXCEL_UPLOAD',`[엑셀 시트 정보] 시트가 1개만 있음. 선택된 시트: "${sheetName}"`);
              
              const worksheet = workbook.Sheets[sheetName];
              
              // 시트의 범위 확인
              const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
              DailyReportDebugLogger.log('EXCEL_UPLOAD','[엑셀 시트 범위]:', {
                시작: `${XLSX.utils.encode_cell(range.s)}`,
                끝: `${XLSX.utils.encode_cell(range.e)}`,
                행수: range.e.r - range.s.r + 1,
                열수: range.e.c - range.s.c + 1
              });
              
              // 첫 번째 행(헤더) 직접 읽기
              DailyReportDebugLogger.log('EXCEL_UPLOAD','[헤더 직접 읽기]:');
              for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({r: range.s.r, c: col});
                const cell = worksheet[cellAddress];
                if (cell) {
                  DailyReportDebugLogger.log('EXCEL_UPLOAD',`  - ${cellAddress}: "${cell.v}" (타입: ${cell.t})`);
                }
              }
              
              // 안전한 헤더 처리를 위해 먼저 헤더를 확인
              let jsonData: any[];
              try {
                // 먼저 원시 데이터로 읽어서 구조 확인
                const rawData = XLSX.utils.sheet_to_json(worksheet, {
                  header: 1,  // 배열로 읽기
                  defval: '',
                  raw: false,
                  blankrows: false
                });
                
                DailyReportDebugLogger.log('EXCEL_UPLOAD','[원시 데이터 확인] 첫 5행:', rawData.slice(0, 5));
                
                // 헤더가 있는 경우 일반적인 방식으로 읽기
                jsonData = XLSX.utils.sheet_to_json(worksheet, {
                  defval: '',
                  raw: false,
                  dateNF: 'yyyy-mm-dd',
                  blankrows: false
                });
              } catch (parseError) {
                DailyReportDebugLogger.error('ERROR_HANDLING','[엑셀 파싱 오류]:', parseError);
                throw new Error('엑셀 데이터를 읽는 중 오류가 발생했습니다.');
              }
              
              if (!jsonData || jsonData.length === 0) {
                setError('시트에 데이터가 없습니다');
                reject(new Error('시트에 데이터가 없습니다'));
                return;
              }
              
              // 컬럼명 정규화 (공백 제거)
              const normalizedData = jsonData.map((row: any) => {
                const normalizedRow: any = {};
                Object.keys(row).forEach(key => {
                  const normalizedKey = key.trim();
                  normalizedRow[normalizedKey] = row[key];
                });
                return normalizedRow;
              });
              
              // 엑셀 데이터 처리
              DailyReportDebugLogger.log('EXCEL_UPLOAD','엑셀 파일 처리 시작:', file.name);
              DailyReportDebugLogger.log('EXCEL_UPLOAD','데이터 개수:', normalizedData.length);
              
              const processedData = processExcelData(normalizedData, file.name);
              
              DailyReportDebugLogger.log('EXCEL_UPLOAD','처리 완료 - 금액 불일치 건수:', processedData.validationErrors.length);
              if (processedData.validationErrors.length > 0) {
                DailyReportDebugLogger.error('ERROR_HANDLING','🚨 금액 불일치가 발견되었습니다!');
                DailyReportDebugLogger.error('ERROR_HANDLING','불일치 목록:', processedData.validationErrors);
              }
              
              setIsLoading(false);
              resolve(processedData);
            } else {
              setError('엑셀 파일에 시트가 없습니다');
              reject(new Error('엑셀 파일에 시트가 없습니다'));
            }
          } catch (error) {
            setError(error instanceof Error ? error.message : '엑셀 파일 처리 중에 오류가 발생했습니다');
            reject(error);
          } finally {
            setIsLoading(false);
          }
        };
        
        reader.onerror = () => {
          setError('파일 읽기 중에 오류가 발생했습니다');
          setIsLoading(false);
          reject(new Error('파일 읽기 중에 오류가 발생했습니다'));
        };
        
        reader.readAsArrayBuffer(file);
      });
    } catch (error) {
      setError(error instanceof Error ? error.message : '엑셀 파일 처리 중에 오류가 발생했습니다');
      setIsLoading(false);
      throw error;
    }
  }, [processExcelData]);

  return {
    processFile,
    extractDateFromFileName,
    isLoading,
    error
  };
};

// Default export
export default useExcelProcessor;